import { Box, Dialog, DialogActions, DialogContent, Grid, Pagination } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { MprcContainer, MprcDialogTitleClose, MprcRequestErrorReport, MprcRequestIndicator, MprcTextMuted } from "components";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { MprcFilterBox, MprcFilterCount, MprcFilterLine } from "components/filter";
import { QuestionChallengeListResponse, QuestionChallengeListResponsePaginatedList } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";

/////////////////////////////////////////////////////////////////////////////////

interface IQuestionFilterOptions {
    searchText: string;
}

interface IViewDataFilter {
    pageNumber: number;
    filter: IQuestionFilterOptions;
}

interface IPros {
    questionGroupId: string;
    title: string;
    onClose: () => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditTopicChallengeViewQuestionsDialog: React.FC<IPros> = (props) => {

    const { questionGroupId, title, onClose } = props;
    const { t } = useTranslation("assessment");
    const [viewData, setViewData] = useState<QuestionChallengeListResponsePaginatedList | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [dialog, setDialog] = useState<any>();
    const api = useCoreApi();

    const viewFilter = useRef<IViewDataFilter>({
        pageNumber: 1,
        filter: { searchText: "" }
    });

    const searchTextField = useRef<MprcSearchTextFieldHandle | null>(null);

    const filtered = (viewFilter.current?.filter.searchText.length || 0) > 0;

    const viewQuestion = (question: QuestionChallengeListResponse) => {
        setDialog(<EditQuestion
            questionId={question.questionId}
            questionGroupId={questionGroupId}
            questionType={question?.questionType}
            forSurvey={false}
            readonly
            onClose={(success) => setDialog(null)}
        />);
    };

    ///
    const resetFilters = () => {
        searchTextField.current?.reset();
        viewFilter.current = {
            pageNumber: 1,
            filter: { searchText: "" }
        };
        setLoading(true);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar a lista
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.questionBank.questionsChallenge({
                    pageNumber: viewFilter.current.pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    questionGroupId,
                    searchText: viewFilter.current.filter.searchText ?? null,
                });
                setViewData(result.data);
            } catch (e) {
                setViewData(null);
                setViewError(e);
            }
            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const Filter = () => {
        return (
            <MprcFilterBox>
                <MprcFilterLine>
                    <MprcSearchTextField
                        autoFocus
                        initialValue={viewFilter.current.filter.searchText}
                        confirmMode="debounce"
                        ref={searchTextField}
                        onConfirm={(value) => {
                            viewFilter.current.filter.searchText = value;
                            viewFilter.current.pageNumber = 1;
                            setLoading(true);
                        }}
                        placeholder={t("shared:Search.TextPlaceholder")}
                    />
                </MprcFilterLine>
            </MprcFilterBox>
        );
    };

    ///
    const RenderGrid = (
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
            {
                viewData?.items.map((item, index) => {
                    return (
                        <Grid key={index} width="100%" size={{ xs: 12 }}>
                            <Grid container justifyContent="center" alignItems="center" spacing={1} wrap="nowrap">
                                <Grid size="grow" overflow="hidden">
                                    <Grid container alignItems="center" flexWrap="nowrap" width="100%">
                                        <Grid overflow="hidden">
                                            <MprcListItemTitle onClick={() => viewQuestion(item)} color="muted">
                                                <MprcTextMuted>
                                                    {item.questionText}
                                                </MprcTextMuted>
                                            </MprcListItemTitle>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    );
                })
            }
        </Grid>
    );

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={() => onClose()}
        >
            <MprcDialogTitleClose
                title={t("TopicChallenge.Edit.ViewQuestions.TitleView")}
                subtitle={title}
                children={
                    <Box marginTop={2}>
                        <MprcContainer>
                            {Filter()}
                        </MprcContainer>

                        <MprcFilterCount
                            loading={false}
                            isEmpty={(viewData?.totalRows ?? 0) == 0}
                            countText={t("TopicChallenge.Edit.ViewQuestions.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                            filter={{
                                active: filtered,
                                text: t("TopicChallenge.Edit.ViewQuestions.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                                onResetFilter: resetFilters
                            }}
                        />
                    </Box>
                }
                onClose={() => onClose()}
                shrinkPadding
            />
            <DialogContent style={{ height: "40vh" }}>
                <MprcRequestErrorReport error={viewError} />
                <MprcRequestIndicator type={"request"} visible={loading} />

                {dialog}

                {RenderGrid}

            </DialogContent>
            <DialogActions style={{ justifyContent: "center" }}>
                {
                    !loading && viewData && viewData?.totalPages > 1 &&
                    <Pagination
                        shape="rounded"
                        color="primary"
                        page={viewData?.pageNumber}
                        count={viewData?.totalPages}
                        onChange={(e, pageNumber) => {
                            viewFilter.current.pageNumber = pageNumber;
                            setLoading(true);
                        }}
                    />
                }
            </DialogActions>
        </Dialog>
    );
};
